import {
  translateRange,
  getSequenceWithinRange,
  flipContainedRange,
  isPositionWithinRange
} from "@teselagen/range-utils";
import revComp from "./getReverseComplementSequenceString";
import getAA from "./getAminoAcidFromSequenceTriplet";
import proteinAlphabet from "./proteinAlphabet";
function getNextTriplet(index, sequenceString, exonRange) {
  let triplet = "";
  let internalIndex;
  const codonPositions = [];
  const isBaseInExon = (baseIndex) => exonRange.some(
    (r) => isPositionWithinRange(baseIndex, r, sequenceString.length, true, false)
  );
  for (internalIndex = index; internalIndex < sequenceString.length; internalIndex++) {
    if (triplet.length === 3) {
      break;
    }
    if (isBaseInExon(internalIndex)) {
      triplet += sequenceString[internalIndex];
      codonPositions.push(internalIndex);
    }
  }
  return { triplet, basesRead: internalIndex - index, codonPositions };
}
function getTranslatedSequenceProperties(originalSequenceString, forward, optionalSubrangeRange, isProteinSequence) {
  const originalSequenceStringLength = isProteinSequence ? originalSequenceString.length * 3 : originalSequenceString.length;
  let sequenceString = originalSequenceString;
  const translationRange = { start: 0, end: originalSequenceStringLength - 1 };
  if (optionalSubrangeRange) {
    sequenceString = getSequenceWithinRange(
      optionalSubrangeRange,
      originalSequenceString
    );
    translationRange.start = isProteinSequence ? optionalSubrangeRange.start * 3 : optionalSubrangeRange.start;
    translationRange.end = isProteinSequence ? (optionalSubrangeRange.end + 1) * 3 - 1 : optionalSubrangeRange.end;
  }
  const sequenceStringLength = isProteinSequence ? sequenceString.length * 3 : sequenceString.length;
  if (!isProteinSequence && !forward) {
    sequenceString = revComp(sequenceString);
  }
  const absoluteExonRange = !isProteinSequence && optionalSubrangeRange && optionalSubrangeRange.locations ? optionalSubrangeRange.locations : [translationRange];
  const exonRange = absoluteExonRange.map((range) => {
    let outputRange = translateRange(
      range,
      -translationRange.start,
      originalSequenceStringLength
    );
    if (!forward) {
      outputRange = flipContainedRange(
        outputRange,
        { start: 0, end: sequenceStringLength - 1 },
        sequenceStringLength
      );
    }
    return outputRange;
  });
  return {
    sequenceString,
    translationRange,
    sequenceStringLength,
    originalSequenceStringLength,
    exonRange
  };
}
function positionInCdsToPositionInMainSequence(index, forward, translationRange, mainSequenceLength) {
  let outputRange = translateRange(
    { start: index, end: index },
    translationRange.start,
    mainSequenceLength
  );
  if (!forward) {
    outputRange = flipContainedRange(
      outputRange,
      translationRange,
      mainSequenceLength
    );
  }
  return outputRange.start;
}
export default function getAminoAcidDataForEachBaseOfDna(originalSequenceString, forward, optionalSubrangeRange, isProteinSequence) {
  if (!originalSequenceString) {
    return [];
  }
  const {
    sequenceString,
    translationRange,
    sequenceStringLength,
    originalSequenceStringLength,
    exonRange
  } = getTranslatedSequenceProperties(
    originalSequenceString,
    forward,
    optionalSubrangeRange,
    isProteinSequence
  );
  const aminoAcidDataForEachBaseOfDNA = [];
  for (let index = 0; index < sequenceStringLength; index += 3) {
    let aminoAcid;
    const aminoAcidIndex = Math.floor(index / 3);
    let codonPositionsInCDS;
    let basesRead;
    if (isProteinSequence) {
      codonPositionsInCDS = [0, 1, 2].map((i) => index + i);
      basesRead = 3;
      aminoAcid = proteinAlphabet[sequenceString[index / 3].toUpperCase()];
    } else {
      const {
        triplet,
        basesRead: _basesRead,
        codonPositions
      } = getNextTriplet(index, sequenceString, exonRange);
      basesRead = _basesRead;
      codonPositionsInCDS = codonPositions;
      aminoAcid = triplet.length === 3 ? getAA(triplet) : getAA("xxx");
    }
    const absoluteCodonPositions = codonPositionsInCDS.map(
      (i) => positionInCdsToPositionInMainSequence(
        i,
        forward,
        translationRange,
        originalSequenceStringLength
      )
    );
    const codonRange = forward ? {
      start: absoluteCodonPositions[0],
      end: absoluteCodonPositions[codonPositionsInCDS.length - 1]
    } : {
      start: absoluteCodonPositions[codonPositionsInCDS.length - 1],
      end: absoluteCodonPositions[0]
    };
    let positionInCodon = 0;
    for (let i = 0; i < basesRead; i++) {
      const posInCds = i + index;
      const sequenceIndex = codonPositionsInCDS.includes(posInCds) ? absoluteCodonPositions[codonPositionsInCDS.indexOf(posInCds)] : positionInCdsToPositionInMainSequence(
        posInCds,
        forward,
        translationRange,
        originalSequenceStringLength
      );
      if (codonPositionsInCDS.includes(posInCds)) {
        aminoAcidDataForEachBaseOfDNA.push({
          aminoAcid,
          positionInCodon,
          aminoAcidIndex,
          sequenceIndex,
          codonRange,
          fullCodon: codonPositionsInCDS.length === 3
        });
        positionInCodon++;
      } else {
        aminoAcidDataForEachBaseOfDNA.push({
          aminoAcid: null,
          positionInCodon: null,
          aminoAcidIndex: null,
          sequenceIndex,
          codonRange: null,
          fullCodon: null
        });
      }
    }
    index += basesRead - codonPositionsInCDS.length;
  }
  if (sequenceStringLength !== aminoAcidDataForEachBaseOfDNA.length) {
    throw new Error("something went wrong!");
  }
  if (!forward) {
    aminoAcidDataForEachBaseOfDNA.reverse();
  }
  return aminoAcidDataForEachBaseOfDNA;
}
